/* ==========================================================================
   AKIKOAOKI — header and mobile behaviour
   Loaded (deferred) by snippets/aki-head.liquid. Config comes from
   window.akiHeader, rendered there by Liquid.

   Moved from six inline <script> blocks in layout/theme.liquid (2026-09-30).
   Changes from the inline version:
   - LOGIN link uses routes.account_login_url, so /en (and any future
     locale) keeps its prefix. On phones the theme's own menu link is used.
   - Initial cart count comes from Liquid; updates follow the theme's own
     cart count element. The 3-second /cart.js poll is gone.
   - If the text links cannot be injected, html.aki-header-failed is set
     and aki-header.css brings the theme icons back.
   - Mobile product slider fix also runs when the viewport becomes mobile,
     and is no longer included twice.
   - Back-navigation image fix clears the theme's .hover class instead of
     forcing display styles for 100 ms.
   ========================================================================== */
(function () {
  'use strict';

  var config = window.akiHeader || {};
  var strings = config.strings || {};
  var loginUrl = config.loginUrl || '/account/login';
  var MOBILE = window.matchMedia('(max-width: 749px)');

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* Run now, then again for late-rendering theme components. */
  function runWithRetries(fn) {
    onReady(fn);
    setTimeout(fn, 500);
    setTimeout(fn, 1500);
    document.addEventListener('shopify:section:load', fn);
  }

  /* ── 1. SEARCH / LOGIN / CART text links ─────────────────────────────── */

  var cartTextElement = null;
  var cartLabel = strings.cart || 'CART';

  function setCartCount(count) {
    if (cartTextElement && count !== undefined && count !== null) {
      cartTextElement.textContent = cartLabel + ' ' + count;
    }
  }

  function fetchAndUpdateCart() {
    fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (data) { setCartCount(data.item_count); })
      .catch(function () {});
  }

  function findSearchButton(header) {
    return (
      header.querySelector('[aria-label*="search" i]') ||
      header.querySelector('button[name="search"]') ||
      header.querySelector('a[href*="/search"]') ||
      header.querySelector('.header__icon--search')
    );
  }

  function findCartButton(header) {
    return (
      header.querySelector('[aria-label*="cart" i]') ||
      header.querySelector('a[href*="/cart"]') ||
      header.querySelector('.header__icon--cart')
    );
  }

  function makeTextButton(label, target) {
    var el = document.createElement('span');
    el.className = 'aki-header-text';
    el.textContent = label;
    el.addEventListener('click', function () {
      target.click();
      target.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      target.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
    });
    return el;
  }

  /* The theme re-renders .thb-item-count after every cart change.
     Watch it and re-read the real count once per change. */
  function watchThemeCartCount(cartBtn) {
    if (cartBtn._akiCartWatched) return;
    cartBtn._akiCartWatched = true;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(fetchAndUpdateCart, 100);
    }).observe(cartBtn, { childList: true, subtree: true, characterData: true });
  }

  function replaceIcons() {
    var header = document.querySelector('theme-header#header');
    if (!header || header.querySelector('.aki-header-wrapper')) return;

    var searchBtn = findSearchButton(header);
    var cartBtn = findCartButton(header);
    if (!searchBtn || !cartBtn || !cartBtn.parentElement) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'aki-header-wrapper';

    var searchText = makeTextButton(strings.search || 'SEARCH', searchBtn);

    var loginText = document.createElement('a');
    loginText.className = 'aki-header-text';
    loginText.href = loginUrl;
    loginText.textContent = strings.login || 'LOGIN';

    cartTextElement = makeTextButton(cartLabel + ' 0', cartBtn);
    setCartCount(config.cartCount);

    wrapper.appendChild(searchText);
    wrapper.appendChild(loginText);
    wrapper.appendChild(cartTextElement);
    cartBtn.parentElement.appendChild(wrapper);

    document.documentElement.classList.remove('aki-header-failed');
    watchThemeCartCount(cartBtn);
  }

  runWithRetries(replaceIcons);

  /* Failsafe: if the links never appeared, give the theme icons back. */
  setTimeout(function () {
    if (!document.querySelector('.aki-header-wrapper')) {
      document.documentElement.classList.add('aki-header-failed');
      console.warn('[aki-header] SEARCH/LOGIN/CART could not be injected; theme icons restored.');
    }
  }, 3000);

  ['cart:updated', 'cart:refresh', 'shopify:cart:change'].forEach(function (name) {
    document.addEventListener(name, fetchAndUpdateCart);
  });

  /* Back/forward cache and returning to the tab can show a stale count. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) fetchAndUpdateCart();
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) fetchAndUpdateCart();
  });

  /* ── 2. Header colour / hover rules (fights theme scroll classes) ────── */

  function applyHeaderRules() {
    var header = document.querySelector('theme-header#header');
    if (!header) return;

    header.classList.add('aki-force');
    header.classList.remove('header--shadow-small');
    header.classList.remove('header--scrolled');
    header.classList.remove('header--sticky');

    header.style.boxShadow = 'none';
    header.style.border = '0';

    // Desktop only: hover shows white background
    var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (canHover && !header._akiHoverBound) {
      header._akiHoverBound = true;
      header.addEventListener('pointerenter', function () {
        header.classList.add('aki-hover-bg');
      }, { passive: true });
      header.addEventListener('pointerleave', function () {
        header.classList.remove('aki-hover-bg');
      }, { passive: true });
    }

    header.querySelectorAll('a, button, span').forEach(function (el) {
      el.style.color = 'var(--aki-black)';
    });
  }

  onReady(applyHeaderRules);

  var scrollTimeout;
  window.addEventListener('scroll', function () {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(applyHeaderRules, 50);
  }, { passive: true });

  document.addEventListener('shopify:section:load', applyHeaderRules);
  document.addEventListener('shopify:section:reorder', applyHeaderRules);

  /* ── 3. Mobile menu: solid background when open ─────────────────────── */
  /* Login in the phone menu is the theme's own link (see aki-header.css). */

  function watchMenuOpen() {
    var details = document.querySelector('.thb-header-mobile-left details.mobile-toggle-wrapper');
    var header = document.querySelector('theme-header#header');
    if (!details || !header || details._akiWatching) return;
    details._akiWatching = true;

    function update() {
      if (window.innerWidth > 749) return;
      header.classList.toggle('aki-menu-open', details.hasAttribute('open'));
    }

    new MutationObserver(update).observe(details, {
      attributes: true, attributeFilter: ['open', 'class']
    });
    update();
  }

  runWithRetries(watchMenuOpen);

  /* ── 4. Mobile menu: items with no submenu navigate directly ─────────── */

  function fixEmptyMobileMenuItems() {
    if (window.innerWidth > 749) return;

    // Build URL map from desktop nav (has full hrefs, same menu)
    var urlMap = {};
    document.querySelectorAll('full-menu .thb-full-menu--link').forEach(function (a) {
      var text = a.innerText && a.innerText.trim();
      if (text && a.href) urlMap[text] = a.href;
    });

    document.querySelectorAll('.mobile-menu-drawer details.link-container').forEach(function (details) {
      if (details._akiMenuFixed) return;

      var ul = details.querySelector('ul');
      if (!ul) return;
      if (ul.querySelectorAll('li:not(.parent-link-back)').length > 0) return;

      var summary = details.querySelector('summary');
      var text = summary && summary.childNodes[0] && summary.childNodes[0].textContent.trim();
      var href = text && urlMap[text];
      if (!href) return;

      details._akiMenuFixed = true;
      details.classList.add('aki-no-submenu');
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = href;
      });
    });
  }

  runWithRetries(fixEmptyMobileMenuItems);

  /* ── 5. Mobile product slider: undo theme's JS-baked fixed heights ───── */

  function fixSlider() {
    if (!MOBILE.matches) return;
    var slider = document.querySelector('product-slider#Product-Slider');
    if (!slider) return;

    slider.style.setProperty('grid-template-rows', 'auto', 'important');
    slider.style.setProperty('height', 'auto', 'important');
    slider.style.setProperty('grid-auto-rows', 'auto', 'important');

    var container = slider.closest('.product-image-container');
    if (container) container.style.setProperty('height', 'auto', 'important');

    if (slider._akiSliderWatched) return;
    slider._akiSliderWatched = true;
    new MutationObserver(function () {
      if (MOBILE.matches && slider.style.gridTemplateRows && slider.style.gridTemplateRows !== 'auto') {
        slider.style.setProperty('grid-template-rows', 'auto', 'important');
        slider.style.setProperty('height', 'auto', 'important');
      }
    }).observe(slider, { attributes: true, attributeFilter: ['style'] });
  }

  onReady(function () {
    setTimeout(fixSlider, 100);
    setTimeout(fixSlider, 500);
    setTimeout(fixSlider, 1500);
  });
  MOBILE.addEventListener('change', fixSlider);

  /* ── 6. Product cards: clear stuck hover image on back navigation ────── */
  /* iOS fires emulated mouseenter/mousemove after a tap. The theme's
     ProductCard (app.js) treats those as hover and adds .hover to a
     secondary image; with no mouseleave, the class is still set when the
     page goes into the back/forward cache, so Back shows the second image.
     Clear the theme's hover state as the page is hidden, and again if a
     cached page is restored. */

  function resetProductCardHover() {
    document.querySelectorAll('.product-secondary-image.hover').forEach(function (img) {
      img.classList.remove('hover');
    });
    document.querySelectorAll('.product-secondary-images-nav li.active').forEach(function (li) {
      li.classList.remove('active');
    });
  }

  window.addEventListener('pagehide', resetProductCardHover);
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) resetProductCardHover();
  });
})();
